import Layout from '../../components/UI/Layout';
import Image from '../../components/UI/Image';
import Link from 'components/UI/Link';
import OfficeDetails, { OfficeHours } from 'components/UI/OfficeDetails';
import RichText from 'components/UI/Richtext';

import type { EntryCollection } from 'contentful';

import React, { ReactElement } from 'react';
import cmsClient from '../../lib/cms/client';
import reviewIncClient from '../../lib/reviewinc/client';
import {
  BrandedSiteProps,
  toSlug,
  getServerSidePropsWithErrors,
  getContentfulPageData,
  cacheDirective,
  PageDates,
  getContentfulSiteData,
  isInvalid,
  withSiteData,
} from '../../lib/routing';
import {
  IMetaLocationClosedDates,
  IMetaLocationsSharedDataFields,
  IPageDoctorFields,
  IPageLocation,
  IPageLocationFields,
} from 'types/contentful';
import { useRouter } from 'next/router';
import { ChevronLeft } from '@material-ui/icons';
import AddressBlock from 'components/UI/AddressBlock';
import { doctorDisplayName, removeIfUndefined } from 'lib/util';
import ScheduleAppointmentCta from 'components/UI/ScheduleAppointmentCta';
import CTA from 'components/UI/Cta';
import { LOCATION_SELECT_FIELDS } from 'lib/consts';
import SharedLocationDataContext from 'context/SharedLocationDataContext';
import {
  getReviewAverage,
  getReviewsWithComments,
  Review,
  ReviewAverage,
  reviewFragmentToRequest,
  reviewToTestimoial,
} from 'lib/reviewinc';
import Score from 'components/UI/Reviews/Score';
import InsuranceProviders from 'components/UI/InsuranceProviders';
import Testimonial from 'components/Contentful/Testimonials';
import { reviewSchema } from 'components/UI/Reviews';
import SchemaOrg from 'components/StructuredData/SchemaOrg';
import { buildComponent } from 'lib/cms/links';
import {
  translateServerSideComponent,
  PreparedComponentObject,
  DynamicComponentTypes,
  buildDynamicComponent,
} from 'components';
import DoctorLocationsAccordian from 'components/UI/Doctor/DoctorLocationsAccordian';

type DoctorProps<K extends DynamicComponentTypes = DynamicComponentTypes, CP = unknown> = {
  preview: boolean;
  content: IPageDoctorFields;
  reviewAvg?: ReviewAverage;
  reviews?: Review[];
  officeInfo: IPageLocationFields | null;
  hideEyeOnPin: boolean;
  siteData: BrandedSiteProps;
  currentSlug: string;
  paymentsAccepted: string[];
  secondaryLocations: IPageLocationFields[] | [];
  closedDates?: IMetaLocationClosedDates;
  pageDates?: PageDates;
  scheduleApptCtaText: string;
  insuranceDisclaimer: string;
  insuranceSlug: string;
  markerIcon?: string;
  doctorVideo?: PreparedComponentObject<K, CP>[] | null;
};

export default function Doctor({
  preview,
  siteData,
  content,
  reviewAvg,
  reviews,
  officeInfo,
  secondaryLocations,
  hideEyeOnPin,
  currentSlug,
  paymentsAccepted,
  closedDates,
  pageDates,
  scheduleApptCtaText,
  insuranceDisclaimer,
  insuranceSlug,
  markerIcon = 'Eye-V01',
  doctorVideo,
}: DoctorProps): ReactElement {
  const router = useRouter();
  const showBackButton = router.query?.office;
  const fullNameAndTitles = doctorDisplayName(content);
  const nextSeoProps = {
    description: `${
      officeInfo?.city ? `${officeInfo?.city} based eye doctor` : 'Doctor'
    } ${fullNameAndTitles} diagnoses, treats, and prescribes for a variety of eye conditions. Schedule an exam at ${
      siteData.siteName
    }.`,
    title: `${fullNameAndTitles}${officeInfo?.city ? ' ' + officeInfo?.city : ''} ${
      content.typeOfDoctor
    }`,
  };

  const doctorVideoComponents = doctorVideo?.map((comp, index: number) => {
    const dynamicComp = buildDynamicComponent(comp, siteData.siteName, preview);

    if (dynamicComp === undefined) return;

    return (
      <div className="mx-auto" key={index}>
        {dynamicComp}
      </div>
    );
  });

  return (
    <Layout
      siteData={siteData}
      preview={preview}
      nextSeoProps={nextSeoProps}
      currentSlug={currentSlug}
      pageDates={pageDates}
    >
      {reviews && reviewAvg && (
        <SchemaOrg
          json={{
            '@context': 'https://schema.org',
            ...reviewSchema({
              type: 'doctor',
              outOfFive: reviewAvg.fiveStarAvg,
              count: reviewAvg.count,
              reviews,
            }),
          }}
        />
      )}
      <SharedLocationDataContext.Provider
        value={{ scheduleAppointmentCtaText: scheduleApptCtaText }}
      >
        {showBackButton && (
          <Link slug={`/locations/${router.query?.office}`}>
            <a className="items-center text-lg hidden md:flex pt-12 pb-6 ml-16">
              <ChevronLeft fontSize="large" /> Back
            </a>
          </Link>
        )}

        {officeInfo && (
          <ScheduleAppointmentCta
            location={officeInfo}
            colorScheme="Primary"
            wrapperClasses="hidden p-6"
          />
        )}

        {/* Hero section */}
        <div className="flex flex-col md:flex-row md:py-10 lg:py-12 lg:px-16 lg:pr-0 bg-muted">
          <div className="w-1/2 md:w-1/3 lg:w-1/4 mt-6 ml-4 md:ml-2 md:mt-0 md:self-center">
            <Image
              image={content.doctorPhoto}
              query="w=280&h=360"
              classNames="object-cover w-full"
            />
          </div>
          <div className="px-4 md:px-0 md:w-2/3 lg:px-0 mt-4 lg:mt-0 md:ml-8">
            <div className="">
              <h3 className="font-heading text-3xl md:mb-6 lg:text-4xl">
                {fullNameAndTitles}
                <div className="flex justify-left">
                  {reviewAvg && (
                    <Score
                      doctorId={content.id}
                      reviewCount={reviewAvg.count}
                      reviewOutOfFive={reviewAvg.fiveStarAvg}
                    />
                  )}
                </div>
              </h3>
            </div>
            <div className="flex flex-col md:flex-row">
              <div className="flex flex-col md:mr-6 lg:mr-32">
                <span className="block text-xl mt-3 md:mt-0">
                  {content.typeOfDoctor} at {content.site.fields.name}
                </span>
                <span className="block text-lg my-4">
                  Accepting new patients?
                  <span className="ml-2 text-tertiary-actual">
                    {content.acceptingNewPatients ? 'Yes' : 'No'}
                  </span>
                </span>
                {officeInfo && (
                  <>
                    <h3 className="text-2xl bold leading-none mb-1">{officeInfo.name}</h3>
                    <AddressBlock {...officeInfo} />
                  </>
                )}
                {!officeInfo && (
                  <div className="py-4 md:py-0">
                    <CTA text="See Locations" slug="locations" colorScheme="Outlined" />
                  </div>
                )}
                {secondaryLocations.length > 1 && (
                  <button
                    onClick={() => {
                      const el = document.getElementById('my_locations');
                      if (el) {
                        const yOffset = 360;
                        const y = el.getBoundingClientRect().top + window.scrollY + yOffset;

                        window.scrollTo({ top: y, behavior: 'smooth' });
                      }
                    }}
                    className="flex text-start bold text-tertiary-actual text-sm lg:text-lg mt-2 md:mt-6 focus:outline-none"
                    type="button"
                    title="Jump to more locations section"
                    aria-label="Jump to more locations section"
                  >
                    {`View ${secondaryLocations.length - 1} More Location${
                      secondaryLocations.length - 1 === 1 ? '' : 's'
                    }`}
                  </button>
                )}
              </div>

              {/* component for specialties or languages */}
              {((content.specialties && content.specialties.length > 0) || content.languages) && (
                <div className="flex flex-col md:flex-row md:border-l-2 md:border-black">
                  {/* specialties */}
                  {content.specialties && content.specialties.length > 0 && (
                    <div className="my-6 px-0 md:pl-4 md:my-0 lg:px-8 ">
                      <h2 className="font-heading text-base lg:text-xl">Specialties</h2>
                      <div className="flex flex-wrap w-full mt-4">
                        <ul>
                          {content.specialties.map((service, i) =>
                            service.fields.slug ? (
                              <Link key={i + service.fields.service} slug={service.fields.slug}>
                                <li className="list-disc ml-6 lg:w-full">
                                  <a className="w-1/2 md:w-1/2 my-2 text-sm md:text-lg underline cursor-pointer">
                                    {service.fields.service}
                                  </a>
                                </li>
                              </Link>
                            ) : (
                              <li key={i} className="list-disc ml-6 lg:w-full">
                                <span className="w-1/2 md:w-1/2 my-2 text-sm md:text-lg">
                                  {service.fields.service}
                                </span>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    </div>
                  )}
                  {/* languages */}
                  {content.languages && (
                    <div className="my-6 md:px-4 md:my-0 lg:px-8">
                      <h2 className="font-heading text-sm md:text-base lg:text-xl">Languages</h2>
                      <ul className="mt-3">
                        {content.languages.map((language, i) => (
                          <li className="list-disc ml-6 text-sm md:text-lg" key={i + language}>
                            {language}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Doctor video */}
        {doctorVideoComponents && <div className="mt-10">{doctorVideoComponents}</div>}

        {/* About section */}
        {content.about && (
          <div className="py-10 p-4 lg:px-16">
            <h2 className="text-xl mb-3">
              About Dr. {content.firstName} {content.lastName}
            </h2>
            <RichText document={content.about} classNames="prose prose-lg max-w-none"></RichText>
          </div>
        )}

        <div className="py-10">
          {/* jumplinks on mobile */}
          {content.education && (
            <div className="md:hidden px-4 text-tertiary-actual underline font-bold">
              <a href="#education">Education</a>
            </div>
          )}
          {content.honorsAwards && (
            <div className="md:hidden px-4 text-tertiary-actual underline font-bold">
              <a href="#honors_and_awards">Honors and Awards</a>
            </div>
          )}
          {content.membershipsAffiliations && (
            <div className="md:hidden px-4 text-tertiary-actual underline font-bold">
              <a href="#memberships_and_affiliations">Memberships and Affiliations</a>
            </div>
          )}
          {paymentsAccepted.length > 0 && (
            <div className="md:hidden px-4 text-tertiary-actual underline font-bold">
              <a href="#payments_accepted">Payments Accepted</a>
            </div>
          )}

          {secondaryLocations.length > 1 ? (
            <DoctorLocationsAccordian
              id="my_locations"
              locations={secondaryLocations}
              closedDates={closedDates}
              siteName={content.site.fields.name}
              markerIcon={markerIcon}
            />
          ) : (
            officeInfo && (
              <OfficeDetails
                siteName={content.site.fields.name}
                officeName={officeInfo.name}
                hideEyeOnPin={hideEyeOnPin}
                hours={officeInfo.hours as OfficeHours}
                hoursNotes={officeInfo.hoursNotes}
                markerIcon={markerIcon}
                center={{
                  lat: officeInfo.map.lat,
                  lng: officeInfo.map.lon,
                }}
                closedDates={closedDates}
                officeInfo={officeInfo}
              >
                <CTA
                  text="View Location Details"
                  slug={`locations/${officeInfo?.slug}`}
                  colorScheme="Outlined"
                  openInNewTab={false}
                  classNames="mt-6 lg:inline-block"
                />
              </OfficeDetails>
            )
          )}

          {/* grid layout for bottom section */}
          <div className="md:grid md:grid-cols-2">
            {/* Education */}
            {content.education && (
              <div
                className="my-6 px-4 lg:px-16"
                id="education"
                style={{ scrollMarginTop: '7rem' }}
              >
                <h2 className="font-heading text-base lg:text-xl">Education</h2>
                <RichText document={content.education} classNames="mt-3 prose max-w-none" />
              </div>
            )}

            {/* Honors and Awards */}
            {content.honorsAwards && (
              <div
                className="my-6 px-4 lg:px-16"
                id="honors_and_awards"
                style={{ scrollMarginTop: '7rem' }}
              >
                <h2 className="font-heading text-base lg:text-xl">Honors and Awards</h2>
                <RichText document={content.honorsAwards} classNames="mt-3 prose max-w-none" />
              </div>
            )}

            {/* Memberships and Affiliations */}
            {content.membershipsAffiliations && (
              <div
                className="my-6 px-4 lg:px-16"
                id="memberships_and_affiliations"
                style={{ scrollMarginTop: '7rem' }}
              >
                <h2 className="font-heading text-base lg:text-xl">Memberships and Affiliations</h2>
                <RichText
                  document={content.membershipsAffiliations}
                  classNames="mt-3 prose max-w-none"
                />
              </div>
            )}

            {/* payments accepted */}
            {paymentsAccepted.length > 0 && (
              <div
                className="my-6 px-4 lg:px-16"
                id="payments_accepted"
                style={{ scrollMarginTop: '7rem' }}
              >
                <h2 className="font-heading text-base lg:text-xl">Payments Accepted</h2>
                <ul className="mt-3">
                  {paymentsAccepted.map((payment, i) => (
                    <li className="list-disc ml-6" key={`${i}-${payment}`}>
                      {payment}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* testimonials */}
          {reviews && (
            <div className="mb-10 mt-10">
              <Testimonial
                title={`What patients say about Dr. ${content.lastName}`}
                testimonials={reviews.map(reviewToTestimoial)}
                backgroundColor="neutral"
              />
            </div>
          )}
          {/* accepted insurance */}
          {content.insuranceProviders && (
            <div>
              <InsuranceProviders
                insuranceProviders={content.insuranceProviders.map((item) => item.fields)}
                insuranceDisclaimer={insuranceDisclaimer}
                insuranceSlug={insuranceSlug}
              />
            </div>
          )}
        </div>
      </SharedLocationDataContext.Provider>
    </Layout>
  );
}

export const getServerSideProps = getServerSidePropsWithErrors<DoctorProps>(
  async ({ resolvedUrl, query, res, req, preview }) => {
    const slug = toSlug(query?.slug) || '';
    const host = req.headers.host;
    const queryOptions = {
      'fields.site.sys.contentType.sys.id': 'site',
      'fields.site.fields.id': host,
      'fields.slug': slug,
      content_type: 'pageDoctor',
      include: 10,
    };
    const siteData = await getContentfulSiteData({ preview, resolvedUrl, req, res });
    if (isInvalid(siteData)) {
      return siteData;
    }
    const contentfulData = await getContentfulPageData<IPageDoctorFields>({
      res,
      req,
      preview,
      queryOptions,
      redirectInsteadofNotFound: '/locations',
    });

    if (isInvalid(contentfulData)) {
      return withSiteData(contentfulData, siteData);
    }

    const client = cmsClient(preview);
    const content = contentfulData.item.fields;
    let officeInfo: IPageLocationFields | null = null;

    if (content.primaryLocation !== undefined || query?.office !== undefined) {
      // google bot is adding a trailing forward slash so we now check for one and remove it
      // locations slugs in contentful are prevented from having a trailing forward slash
      const slug = query?.office
        ? decodeURIComponent(query?.office as string).replace(/\/$/, '')
        : content.primaryLocation?.fields.slug;

      const locationPage: EntryCollection<IPageLocationFields> = await client.getEntries({
        'fields.site.sys.contentType.sys.id': 'site',
        'fields.site.fields.id': host,
        'fields.slug': slug,
        select: 'fields.hours,fields.hoursNotes,' + LOCATION_SELECT_FIELDS,
        content_type: 'pageLocation',
        include: 1,
      });

      if (locationPage.items.length === 1) {
        officeInfo = { ...locationPage.items[0].fields };
      }
    }

    const cleanSecondaryLocations = content.secondaryLocations
      ? extractFieldsWithoutCircularDoctorDependency(content.secondaryLocations)
      : [];
    const cleanPrimaryLocations = content.primaryLocation
      ? extractFieldsWithoutCircularDoctorDependency([content.primaryLocation])
      : [];

    // delete circular dependency
    delete content.primaryLocation;
    delete content.secondaryLocations;

    const sharedLocationData: EntryCollection<IMetaLocationsSharedDataFields> = await client.getEntries(
      {
        'fields.site.sys.contentType.sys.id': 'site',
        'fields.site.fields.id': host,
        select:
          'fields.paymentsAccepted,fields.closedDates,fields.scheduleApptCtaText,fields.insuranceDisclaimer,fields.insuranceSlug,fields.hideEyeIcon,fields.markerPinIcon',
        content_type: 'metaLocationsSharedData',
        include: 1,
      }
    );

    const doctorVideo = content.doctorVideo
      ? await Promise.all(
          [content.doctorVideo]
            .map((item) => buildComponent(item?.sys?.contentType?.sys?.id, item?.fields))
            .map((item) => translateServerSideComponent(item, !!preview))
        )
      : null;

    const reviewAvg =
      content.reviewsIds &&
      (await getReviewAverage(reviewIncClient, reviewFragmentToRequest(content.reviewsIds)));

    const reviews =
      content.reviewsIds &&
      (await getReviewsWithComments(reviewIncClient, reviewFragmentToRequest(content.reviewsIds)));

    res.setHeader('Cache-Control', cacheDirective(preview));

    return {
      props: {
        preview: preview ? true : false,
        officeInfo,
        ...removeIfUndefined({ closedDates: sharedLocationData?.items[0]?.fields?.closedDates }),
        paymentsAccepted: sharedLocationData?.items[0]?.fields?.paymentsAccepted || [],
        scheduleApptCtaText: sharedLocationData?.items[0]?.fields?.scheduleApptCtaText || '',
        hideEyeOnPin: !!sharedLocationData?.items[0]?.fields?.hideEyeIcon,
        secondaryLocations: [...cleanPrimaryLocations, ...cleanSecondaryLocations],
        markerIcon: sharedLocationData?.items[0]?.fields?.markerPinIcon || '',
        insuranceDisclaimer: sharedLocationData?.items[0]?.fields?.insuranceDisclaimer || '',
        insuranceSlug: sharedLocationData?.items[0]?.fields?.insuranceSlug || '',
        siteData,
        content,
        ...removeIfUndefined({ reviewAvg, reviews }),
        currentSlug: `doctors/${slug}`,
        pageDates: {
          datePublished: contentfulData.item.sys.createdAt,
          dateModified: contentfulData.item.sys.updatedAt,
        },
        doctorVideo,
      },
    };
  }
);

function extractFieldsWithoutCircularDoctorDependency(
  data: IPageLocation[]
): IPageLocationFields[] {
  return data.map((item) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { doctors, ...fieldsWithoutDoctors } = item.fields;
    return fieldsWithoutDoctors;
  });
}
